import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

interface CacheNode<T = unknown> {
  value: T;
  expiresAt: ReturnType<Date['getTime']>;
}

@Injectable({
  providedIn: 'root',
})
export class CacheManager {
  public readonly cache = new Map<string, CacheNode<unknown>>();

  /**
   *  Retrieve node from cache only if exists and if its expiration
   * @param {string} key The key to access node from cache
   * @returns {Observable<unknown> | undefined} Returns undefined if node/key doesn't exist or if it's expired otherwise returns the node value as an Observable
   */
  public get(key: string): Observable<unknown> | undefined {
    if (!key) return undefined;

    const node = this.cache.get(key);

    if (!node) return undefined;

    const now = new Date().getTime();

    if (now > node.expiresAt) {
      this.cache.delete(key);
      return undefined;
    }

    return of(node.value);
  }

  /**
   * Set a new node to the cache only if it doesn't exist yet
   * @param {string} key  The key to access node from cache
   * @param {T} value Any value to add to the cache
   * @param {number} ttl (time to live) Time during the node has to stay accessible. `In milliseconds`
   * @returns {Observable<any> | Error} Returns an Observable of the node value if it doesn't exist yet. Otherwise a new error is returned
   */
  public set<T>(
    key: string,
    value: T,
    ttl: number = 300000,
  ): Observable<unknown> | Error {
    if (this.cache.has(key))
      return new Error('Node already exists in the cache');

    const willExpiresAt = new Date().getTime() + ttl;
    this.cache.set(key, { expiresAt: willExpiresAt, value });

    return of(value);
  }
}
